import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import { FlightSearchResponse, TripSegment } from "@/types/search/flight";
import Image from "next/image";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

// Add type for route metadata
interface RouteMetadata {
  fromCityOrAirport: string;
  toCityOrAirport: string;
  index: number;
}

// Add interface for segment type
interface FlightSegment {
  sN: number;
  dt: string;
  at: string;
  isRs: boolean;
  fD: {
    aI: {
      name: string;
    };
  };
}

interface TripInfo {
  isRoundTrip: boolean;
  routes: Array<{ from: string; to: string }>;
  isMuktiCity?: boolean;
}

interface RouteInfo {
  fromCityOrAirport: {
    code: string;
  };
  toCityOrAirport: {
    code: string;
  };
}

interface SearchData {
  routeInfos?: RouteInfo[];
}

// Get route metadata from URL
const getRouteMetadata = (): RouteMetadata[] => {
  const urlParams = new URLSearchParams(window.location.search);
  const metadata = urlParams.get("metadata");
  return metadata ? JSON.parse(metadata) : [];
};

// Time range constants
const TIME_RANGES = {
  EARLY_MORNING: "0-6", // 00:00 - 05:59
  MORNING: "6-12", // 06:00 - 11:59
  AFTERNOON: "12-18", // 12:00 - 17:59
  EVENING: "18-24", // 18:00 - 23:59
} as const;

const Filters = () => {
  // Add new states for onward and return stops
  const [selectedOnwardStops, setSelectedOnwardStops] = useState<number[]>([]);
  const [selectedReturnStops, setSelectedReturnStops] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState("onward");
  const [selectedOnwardAirlines, setSelectedOnwardAirlines] = useState<string[]>([]);
  const [selectedReturnAirlines, setSelectedReturnAirlines] = useState<string[]>([]);
  const [selectedDepartureTimes, setSelectedDepartureTimes] = useState<
    string[]
  >([]);
  const [selectedReturnTimes, setSelectedReturnTimes] = useState<string[]>([]);
  const [selectedOutboundArrivalTimes, setSelectedOutboundArrivalTimes] =
    useState<string[]>([]);
  const [selectedReturnArrivalTimes, setSelectedReturnArrivalTimes] = useState<
    string[]
  >([]);

  // Add state for URL params
  const [urlParams, setUrlParams] = useState<{
    data: SearchData;
    metadata: any[];
    tripType: string;
  }>({
    data: {},
    metadata: [],
    tripType: "",
  });

  const [tripInfo, setTripInfo] = useState<TripInfo>({
    isRoundTrip: false,
    routes: [],
    isMuktiCity: false,
  });

  const {
    searchResults: flightSearchResult,
    setFilteredFlights,
    filteredFlights,
    indexValue,
  } = useFlightBookingStore();

  const stopsOptions = [
    { label: "Non Stop", value: 0 },
    { label: "1 Stop", value: 1 },
    { label: "2 Stops", value: 2 },
    { label: "3+ Stops", value: 3 },
  ];

  // Button time ranges
  const timeRanges = [
    { label: "Before 6AM", value: "0-6" },
    { label: "6AM - 12PM", value: "6-12" },
    { label: "12PM - 6PM", value: "12-18" },
    { label: "After 6PM", value: "18-24" },
  ];

  function formatToRupee(amount: number) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  }

  const getMaxPrice = () => {
    let maxPrice = 0;
    const tripInfos = originalFlights.searchResult.tripInfos;

    const processTrips = (trips: TripSegment[]) => {
      trips.forEach((trip: any) => {
        const price = trip.totalPriceList[0]?.fd?.ADULT?.fC?.TF || 0;
        maxPrice = Math.max(maxPrice, price);
      });
    };

    if ("ONWARD" in tripInfos) {
      processTrips(tripInfos.ONWARD);
      if ("RETURN" in tripInfos) {
        processTrips(tripInfos.RETURN);
      }
    } else if ("COMBO" in tripInfos) {
      processTrips(tripInfos.COMBO);
    } else {
      // For multicity, only process the current tab's trips
      const currentTrips = tripInfos[indexValue];
      if (currentTrips) {
        processTrips(currentTrips);
      }
    }

    return maxPrice || 50000;
  };

  const getMinPrice = () => {
    let minPrice = Infinity;
    const tripInfos = originalFlights.searchResult.tripInfos;

    const processTrips = (trips: TripSegment[]) => {
      trips.forEach((trip: any) => {
        const tf = trip?.totalPriceList?.[0]?.fd?.ADULT?.fC?.TF;
        const price = typeof tf === 'number' ? tf : Infinity;
        minPrice = Math.min(minPrice, price);
      });
    };

    if ("ONWARD" in tripInfos) {
      processTrips(tripInfos.ONWARD);
      if ("RETURN" in tripInfos) {
        processTrips(tripInfos.RETURN);
      }
    } else if ("COMBO" in tripInfos) {
      processTrips(tripInfos.COMBO);
    } else {
      // For multicity, only process the current tab's trips
      const currentTrips = tripInfos[indexValue];
      if (currentTrips) {
        processTrips(currentTrips);
      }
    }

    return minPrice === Infinity ? 0 : minPrice;
  };

  // Make a deep copy of the original flightSearchResult to retain original data
  const originalFlights: FlightSearchResponse = React.useMemo(
    () => ({ ...flightSearchResult } as FlightSearchResponse),
    [flightSearchResult]
  );
  const [priceRange, setPriceRange] = useState([getMinPrice(), getMaxPrice()]);

  // Add new state for stops filter
  const [selectedStops, setSelectedStops] = useState<{
    [key: number]: number[];
  }>({});

  const routeMetadata = getRouteMetadata();

  const [isFilterVisible, setIsFilterVisible] = useState(false);

  // Add URL parameter watcher
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setUrlParams({
      data: JSON.parse(params.get("data") || "{}"),
      metadata: JSON.parse(params.get("metadata") || "[]"),
      tripType: params.get("tripType") || "oneway",
    });
  }, [window.location.search]);

  const getAirlinesWithPrices = () => {
    // Separate maps for onward and return flights
    const onwardAirlineMap: { [key: string]: { price: number; code: string } } =
      {};
    const returnAirlineMap: { [key: string]: { price: number; code: string } } =
      {};

    const addAirline = (
      trips: { sI: any[]; totalPriceList: any[] }[],
      map: any
    ) => {
      trips.forEach((trip: { sI: any[]; totalPriceList: any[] }) => {
        const airlineName = trip.sI[0].fD.aI.name;
        const airlineCode = trip.sI[0].fD.aI.code;
        const price = trip.totalPriceList[0]?.fd?.ADULT?.fC?.TF;

        if (!map[airlineName]) {
          map[airlineName] = { price, code: airlineCode };
        }
      });
    };

    if ("ONWARD" in originalFlights.searchResult.tripInfos) {
      const onwardTrips = originalFlights.searchResult.tripInfos.ONWARD;
      addAirline(onwardTrips, onwardAirlineMap);
      if ("RETURN" in originalFlights.searchResult.tripInfos) {
        const returnTrips = originalFlights.searchResult.tripInfos.RETURN;
        if (returnTrips.length > 0) {
          addAirline(returnTrips, returnAirlineMap);
        }
      }
    } else if ("COMBO" in originalFlights.searchResult.tripInfos) {
      addAirline(
        originalFlights.searchResult.tripInfos.COMBO,
        onwardAirlineMap
      );
    } else {
      // For multicity, only process the current tab's trips
      const currentTrips = originalFlights.searchResult.tripInfos[indexValue];
      if (currentTrips) {
        addAirline(currentTrips, onwardAirlineMap);
      }
    }

    return { onward: onwardAirlineMap, return: returnAirlineMap };
  };

  // Reset price range when original flights change (e.g. on new search or reschedule)
  useEffect(() => {
    if (originalFlights?.searchResult?.tripInfos) {
      setPriceRange([getMinPrice(), getMaxPrice()]);
    }
  }, [originalFlights]);

  const airlinesWithPricesAndCodes = getAirlinesWithPrices();

  // Add new state for combo tabs
  const [activeComboTab, setActiveComboTab] = useState(0);

  // Add effect to preserve selected stops when switching tabs
  useEffect(() => {
    // Make sure we have an entry for both tabs in selectedStops
    setSelectedStops((prev) => {
      const newStops = { ...prev };
      // Initialize any missing tab entries with empty arrays
      if (!newStops[0]) newStops[0] = [];
      if (!newStops[1]) newStops[1] = [];
      return newStops;
    });
  }, []);

  // Add helper function to get route segments for combo flights
  const getComboRouteSegments = (sI: FlightSegment[], routeIndex: number) => {
    if (!sI || !sI.length) return [];

    // Group segments by route (outbound vs return)
    const outboundSegments = sI.filter((segment) => !segment.isRs);
    const returnSegments = sI.filter((segment) => segment.isRs);

    // Return appropriate segments based on routeIndex
    return routeIndex === 0 ? outboundSegments : returnSegments;
  };

  // Add function to get number of stops for combo route
  const getComboRouteStops = (sI: FlightSegment[], routeIndex: number) => {
    const routeSegments = getComboRouteSegments(sI, routeIndex);
    return routeSegments.length - 1;
  };

  const applyFilters = () => {
    let updatedFlightSearchResult = JSON.parse(JSON.stringify(originalFlights));

    const isTimeInRange = (hour: number, timeRange: string): boolean => {
      const [start, end] = timeRange.split("-").map(Number);

      switch (timeRange) {
        case TIME_RANGES.EARLY_MORNING:
          return hour >= start && hour < end; // Strict check until 5:59

        case TIME_RANGES.MORNING:
          return hour >= start && hour < end; // 6:00 to 11:59

        case TIME_RANGES.AFTERNOON:
          return hour >= start && hour < end; // 12:00 to 17:59

        case TIME_RANGES.EVENING:
          return hour >= start && hour < end; // 18:00 to 23:59

        default:
          return false;
      }
    };
    const filterTrips = (trips: any[], timeType: "outbound" | "return") => {
      return trips?.filter((trip) => {
        const price = trip.totalPriceList[0]?.fd?.ADULT?.fC?.TF;
        const departureHours = trip.sI.map((segment: FlightSegment) =>
          new Date(segment.dt).getHours()
        );
        const arrivalHours = trip.sI.map((segment: FlightSegment) =>
          new Date(segment.at).getHours()
        );
        const airlines = trip.sI.map(
          (segment: FlightSegment) => segment.fD.aI.name
        );

        // Calculate stops based on combo route if applicable
        let numberOfStops;
        if ("COMBO" in originalFlights.searchResult.tripInfos) {
          numberOfStops = getComboRouteStops(trip.sI, activeComboTab);
        } else {
          numberOfStops = trip.sI.length - 1;
        }

        // Check if any departure time matches selected ranges
        const isDepartureTimeValid =
          timeType === "return"
            ? selectedReturnTimes.length === 0 ||
            selectedReturnTimes.some((range) =>
              isTimeInRange(departureHours[0], range)
            )
            : selectedDepartureTimes.length === 0 ||
            selectedDepartureTimes.some((range) =>
              isTimeInRange(departureHours[0], range)
            );

        // Check if any arrival time matches selected ranges
        const isArrivalTimeValid =
          timeType === "return"
            ? selectedReturnArrivalTimes.length === 0 ||
            selectedReturnArrivalTimes.some((range) =>
              isTimeInRange(arrivalHours[arrivalHours.length - 1], range)
            )
            : selectedOutboundArrivalTimes.length === 0 ||
            selectedOutboundArrivalTimes.some((range) =>
              isTimeInRange(arrivalHours[arrivalHours.length - 1], range)
            );

        // Check airlines
        const isAirlineValid = timeType === "return"
          ? selectedReturnAirlines.length === 0 ||
          airlines.every((airline: string) =>
            selectedReturnAirlines.includes(airline)
          )
          : selectedOnwardAirlines.length === 0 ||
          airlines.every((airline: string) =>
            selectedOnwardAirlines.includes(airline)
          );
        // Check stops
        const isStopsValid = !tripInfo.isRoundTrip
          ? !selectedStops[0] ||
          selectedStops[0].length === 0 ||
          selectedStops[0].includes(Math.min(numberOfStops, 3))
          : timeType === "return"
            ? selectedReturnStops.length === 0 ||
            selectedReturnStops.includes(Math.min(numberOfStops, 3))
            : selectedOnwardStops.length === 0 ||
            selectedOnwardStops.includes(Math.min(numberOfStops, 3));

        return (
          price >= priceRange[0] &&
          price <= priceRange[1] &&
          isDepartureTimeValid &&
          isArrivalTimeValid &&
          isAirlineValid &&
          isStopsValid
        );
      });
    };

    // create a sprate function to filter combo trips
    const filterComboTrips = (
      trips: any[],
      timeType: "outbound" | "return"
    ) => {
      return trips.filter((trip) => {
        const price = trip.totalPriceList[0]?.fd?.ADULT?.fC?.TF;
        const departureHours = trip.sI.map((segment: FlightSegment) =>
          new Date(segment.dt).getHours()
        );
        const arrivalHours = trip.sI.map((segment: FlightSegment) =>
          new Date(segment.at).getHours()
        );
        const airlines = trip.sI.map(
          (segment: FlightSegment) => segment.fD.aI.name
        );

        // Calculate stops based on combo route if applicable
        let numberOfStops;
        if ("COMBO" in originalFlights.searchResult.tripInfos) {
          numberOfStops = getComboRouteStops(trip.sI, activeComboTab);
        } else {
          numberOfStops = trip.sI.length - 1;
        }
        // Check if any departure time matches selected ranges
        const isDepartureTimeValid =
          timeType === "return"
            ? selectedReturnTimes.length === 0 ||
            departureHours.some((hour: any) =>
              selectedReturnTimes.some((range) => isTimeInRange(hour, range))
            )
            : selectedDepartureTimes.length === 0 ||
            departureHours.some((hour: any) =>
              selectedDepartureTimes.some((range) =>
                isTimeInRange(hour, range)
              )
            );
        // Check if any arrival time matches selected ranges
        const isArrivalTimeValid =
          timeType === "return"
            ? selectedReturnArrivalTimes.length === 0 ||
            arrivalHours.some((hour: any) =>
              selectedReturnArrivalTimes.some((range) =>
                isTimeInRange(hour, range)
              )
            )
            : selectedOutboundArrivalTimes.length === 0 ||
            arrivalHours.some((hour: any) =>
              selectedOutboundArrivalTimes.some((range) =>
                isTimeInRange(hour, range)
              )
            );
        // Check airlines
        const isAirlineValid = timeType === "return"
          ? selectedReturnAirlines.length === 0 ||
          airlines.every((airline: string) =>
            selectedReturnAirlines.includes(airline)
          )
          : selectedOnwardAirlines.length === 0 ||
          airlines.every((airline: string) =>
          (
            selectedOnwardAirlines.includes(airline)
          )
          );


        // Check stops for all tabs
        const outboundStops = getComboRouteStops(trip.sI, 0);
        const returnStops = getComboRouteStops(trip.sI, 1);

        // A flight is valid if it matches the filters for both tabs (or no filters are set)
        const isOutboundStopsValid =
          !selectedStops[0] ||
          selectedStops[0].length === 0 ||
          selectedStops[0].includes(Math.min(outboundStops, 3));

        const isReturnStopsValid =
          !selectedStops[1] ||
          selectedStops[1].length === 0 ||
          selectedStops[1].includes(Math.min(returnStops, 3));

        // Both outbound and return must match their respective filters
        const isStopsValid = isOutboundStopsValid && isReturnStopsValid;

        return (
          price >= priceRange[0] &&
          price <= priceRange[1] &&
          isDepartureTimeValid &&
          isArrivalTimeValid &&
          isAirlineValid &&
          isStopsValid
        );
      });
    };



    if (updatedFlightSearchResult.searchResult.tripInfos.COMBO) {
      updatedFlightSearchResult.searchResult.tripInfos.COMBO = filterComboTrips(
        updatedFlightSearchResult.searchResult.tripInfos.COMBO,
        "outbound"
      );
    } else if (updatedFlightSearchResult.searchResult.tripInfos.ONWARD) {
      updatedFlightSearchResult.searchResult.tripInfos.ONWARD = filterTrips(
        updatedFlightSearchResult.searchResult.tripInfos.ONWARD,
        "outbound"
      );

      if (updatedFlightSearchResult.searchResult.tripInfos.RETURN) {
        updatedFlightSearchResult.searchResult.tripInfos.RETURN = filterTrips(
          updatedFlightSearchResult.searchResult.tripInfos.RETURN,
          "return"
        );
      }
    } else {
      // Multicity logic
      const updatedTripInfos = {
        ...updatedFlightSearchResult.searchResult.tripInfos,
      };

      if (updatedTripInfos) {
        updatedTripInfos[indexValue] = filterTrips(
          updatedTripInfos[indexValue],
          "outbound"
        );
      }

      updatedFlightSearchResult.searchResult.tripInfos = updatedTripInfos;
    }

    setFilteredFlights(updatedFlightSearchResult);
  };

  useEffect(() => {
    applyFilters();
  }, [
    priceRange,
    selectedDepartureTimes,
    selectedReturnTimes,
    selectedOutboundArrivalTimes,
    selectedReturnArrivalTimes,
    selectedOnwardAirlines,
    selectedReturnAirlines,
    selectedReturnAirlines,
    selectedStops,
    selectedOnwardStops,
    selectedReturnStops,
    activeTab,
    activeComboTab,
  ]);

  // Update getTripInfo function
  const getTripInfo = useCallback(() => {
    try {
      const { data, metadata, tripType } = urlParams;

      return {
        isRoundTrip:
          tripType === "roundtrip" ||
          (data.routeInfos?.length === 2 &&
            data.routeInfos[0].fromCityOrAirport.code ===
            data.routeInfos[1].toCityOrAirport.code &&
            data.routeInfos[0].toCityOrAirport.code ===
            data.routeInfos[1].fromCityOrAirport.code),
        routes: metadata.map((route: any) => ({
          from: route.fromCityOrAirport,
          to: route.toCityOrAirport,
        })),
        isMuktiCity:
          tripType === "multicity" ||
          (data.routeInfos && data.routeInfos.length > 2) ||
          (data.routeInfos &&
            data.routeInfos.length === 2 &&
            data.routeInfos[0].fromCityOrAirport.code !==
            data.routeInfos[1].toCityOrAirport.code &&
            data.routeInfos[0].toCityOrAirport.code !==
            data.routeInfos[1].fromCityOrAirport.code),
      };
    } catch (error) {
      console.error("Error in getTripInfo:", error);
      return { isRoundTrip: false, routes: [], isMuktiCity: false };
    }
  }, [urlParams]);

  // Update effect to run when params change
  useEffect(() => {
    setTripInfo(getTripInfo());
  }, [getTripInfo, urlParams]);

  // Add effect to reset filters when index changes for multicity
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tripType = urlParams.get("tripType") || "{}";

    if (tripType === "multicity") {
      // Reset price range to new min/max for current tab
      setPriceRange([getMinPrice(), getMaxPrice()]);
      // Reset airline selection
      setSelectedOnwardAirlines([]);
      setSelectedReturnAirlines([]);
      // Reset other filters as needed
      setSelectedStops([]);
      setSelectedDepartureTimes([]);
      setSelectedOutboundArrivalTimes([]);
    }
  }, [indexValue]);

  return (
    <>
      <div className="block lg:hidden">
        <Button
          variant="outline"
          onClick={() => setIsFilterVisible(!isFilterVisible)}
          className="flex items-center gap-2"
        >
          <Filter size={20} />
          {isFilterVisible ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>

      <div className={`px-3 ${isFilterVisible ? "block" : "hidden"}  lg:block`}>
        <div className="bg-white rounded-lg shadow-xl h-fit box-border">
          <div className="border-b border-gray-200">
            <div className="flex items-start justify-between p-3">
              <div>
                <h6 className="font-bold text-lg text-[#05264e]">Filters</h6>
                <p className="text-md text-[#ada5bd] text-[14px]">
                  Showing{" "}
                  {filteredFlights?.searchResult?.tripInfos
                    ? Object.values(
                      filteredFlights.searchResult.tripInfos
                    ).reduce(
                      (total, tripSegments) =>
                        total + (tripSegments?.length || 0),
                      0
                    )
                    : 0}{" "}
                  Flights
                </p>
              </div>
              <div className="text-right">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setPriceRange([getMinPrice(), getMaxPrice()]);
                    setSelectedOnwardAirlines([]);
                    setSelectedReturnAirlines([]);
                    setSelectedStops([]);
                    setSelectedOnwardStops([]);
                    setSelectedReturnStops([]);
                    setSelectedDepartureTimes([]);
                    setSelectedReturnTimes([]);
                    setSelectedOutboundArrivalTimes([]);
                    setSelectedReturnArrivalTimes([]);
                    setFilteredFlights(originalFlights);
                  }}
                  className="text-md font-medium text-primary text-[14px]"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-6">
            {/* Price Range Filter */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Price Range</h3>
              <Slider
                min={getMinPrice()}
                max={getMaxPrice()}
                step={100}
                value={priceRange}
                onValueChange={(value) => {
                  setPriceRange(value);
                }}
              />
              <div className="flex justify-between mt-2">
                <span>{formatToRupee(priceRange[0])}</span>
                <span>{formatToRupee(priceRange[1])}</span>
              </div>
            </div>

            {/* Stops Filter with Tabs for Roundtrip */}
            <div>
              <h3 className="mb-4 text-md font-medium text-[#05264e]">Stops</h3>
              {"COMBO" in originalFlights?.searchResult?.tripInfos ? (
                <Tabs
                  defaultValue="0"
                  value={activeComboTab.toString()}
                  onValueChange={(value) => setActiveComboTab(parseInt(value))}
                >
                  <TabsList className="w-full mb-4 flex flex-wrap flex-col">
                    {routeMetadata.map((route, index) => (
                      <TabsTrigger
                        key={index}
                        value={index.toString()}
                        className="flex-1"
                      >
                        {`${route.fromCityOrAirport} to ${route.toCityOrAirport}`}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {routeMetadata.map((route, index) => (
                    <TabsContent key={index} value={index.toString()}>
                      <div className="grid grid-cols-3 gap-3">
                        {stopsOptions.map(({ label, value }) => (
                          <button
                            key={value}
                            onClick={() => {
                              setSelectedStops((prev) => {
                                const newStops = { ...prev };
                                if (newStops[index]?.includes(value)) {
                                  newStops[index] = newStops[index].filter(
                                    (stop) => stop !== value
                                  );
                                } else {
                                  newStops[index] = [
                                    ...(newStops[index] || []),
                                    value,
                                  ];
                                }
                                return newStops;
                              });
                            }}
                            className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedStops[index]?.includes(value)
                                ? "bg-primary text-white"
                                : "bg-[#f3f6fa] text-[#738594]"
                              }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              ) : // Original non-combo stops filter code
                tripInfo.isRoundTrip ? (
                  <Tabs
                    defaultValue="onward"
                    onValueChange={(value) => setActiveTab(value)}
                  >
                    <TabsList className="w-full mb-4">
                      <TabsTrigger value="onward" className="flex-1">
                        Onward
                      </TabsTrigger>
                      <TabsTrigger value="return" className="flex-1">
                        Return
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="onward">
                      <div className="grid grid-cols-3 gap-3">
                        {stopsOptions.map(({ label, value }) => (
                          <button
                            key={value}
                            onClick={() => {
                              setSelectedOnwardStops((prev) =>
                                prev.includes(value)
                                  ? prev.filter((stop) => stop !== value)
                                  : [...prev, value]
                              );
                            }}
                            className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedOnwardStops.includes(value)
                                ? "bg-primary text-white"
                                : "bg-[#f3f6fa] text-[#738594]"
                              }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </TabsContent>

                    <TabsContent value="return">
                      <div className="grid grid-cols-3 gap-3">
                        {stopsOptions.map(({ label, value }) => (
                          <button
                            key={value}
                            onClick={() => {
                              setSelectedReturnStops((prev) =>
                                prev.includes(value)
                                  ? prev.filter((stop) => stop !== value)
                                  : [...prev, value]
                              );
                            }}
                            className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedReturnStops.includes(value)
                                ? "bg-primary text-white"
                                : "bg-[#f3f6fa] text-[#738594]"
                              }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                ) : (
                  // Original one-way stops filter
                  <div className="grid grid-cols-3 gap-3">
                    {stopsOptions.map(({ label, value }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setSelectedStops((prev) => {
                            const newStops = { ...prev };
                            // Always use index 0 for one-way flights
                            if (newStops[0]?.includes(value)) {
                              newStops[0] = newStops[0].filter(
                                (stop) => stop !== value
                              );
                            } else {
                              newStops[0] = [...(newStops[0] || []), value];
                            }
                            return newStops;
                          });
                        }}
                        className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedStops[0]?.includes(value)
                            ? "bg-primary text-white"
                            : "bg-[#f3f6fa] text-[#738594]"
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
            </div>

            {/* Departure Time Filter */}
            {!tripInfo.isMuktiCity && (
              <div>
                <h3 className="mb-4 text-md font-medium text-[#05264e]">
                  {!tripInfo.isMuktiCity &&
                    tripInfo.routes[0]?.from &&
                    tripInfo.routes[0]?.to ? (
                    <>Departure from {tripInfo.routes[0].from}</>
                  ) : (
                    "Departure Time"
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {timeRanges.map(({ label, value }) => (
                    <button
                      key={value}
                      onClick={() => {
                        console.log(`Button clicked for time range: ${value}`);
                        setSelectedDepartureTimes((prev) =>
                          prev.includes(value)
                            ? prev.filter((time) => time !== value)
                            : [...prev, value]
                        );
                      }}
                      className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedDepartureTimes.includes(value)
                          ? "bg-primary text-white"
                          : "bg-[#f3f6fa] text-[#738594]"
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Arrival Time Filter for Outbound */}
            {!tripInfo.isMuktiCity && (
              <div>
                <h3 className="mb-4 text-md font-medium text-[#05264e]">
                  {tripInfo.routes[0]?.from && tripInfo.routes[0]?.to ? (
                    <>Arrival at {tripInfo.routes[0].to}</>
                  ) : (
                    "Arrival Time"
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {timeRanges.map(({ label, value }) => (
                    <button
                      key={value}
                      onClick={() => {
                        console.log(`Button clicked for time range: ${value}`);
                        setSelectedOutboundArrivalTimes((prev) =>
                          prev.includes(value)
                            ? prev.filter((time) => time !== value)
                            : [...prev, value]
                        );
                      }}
                      className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedOutboundArrivalTimes.includes(value)
                          ? "bg-primary text-white"
                          : "bg-[#f3f6fa] text-[#738594]"
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Return Time Filter - Only show for round trips */}
            {!tripInfo.isMuktiCity &&
              tripInfo.isRoundTrip &&
              tripInfo.routes[1] && (
                <>
                  <div>
                    <h3 className="mb-4 text-md font-medium text-[#05264e]">
                      {tripInfo.routes[1]?.from && tripInfo.routes[1]?.to ? (
                        <>Departure from {tripInfo.routes[1].from}</>
                      ) : (
                        "Return Departure Time"
                      )}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {timeRanges.map(({ label, value }) => (
                        <button
                          key={value}
                          onClick={() => {
                            console.log(
                              `Button clicked for time range: ${value}`
                            );
                            setSelectedReturnTimes((prev) =>
                              prev.includes(value)
                                ? prev.filter((time) => time !== value)
                                : [...prev, value]
                            );
                          }}
                          className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedReturnTimes.includes(value)
                              ? "bg-primary text-white"
                              : "bg-[#f3f6fa] text-[#738594]"
                            }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Return Arrival Time Filter */}
                  <div>
                    <h3 className="mb-4 text-md font-medium text-[#05264e]">
                      {!tripInfo.isMuktiCity &&
                        tripInfo.routes[1]?.from &&
                        tripInfo.routes[1]?.to ? (
                        <>Arrival at {tripInfo.routes[1].to}</>
                      ) : (
                        "Return Arrival Time"
                      )}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {timeRanges.map(({ label, value }) => (
                        <button
                          key={value}
                          onClick={() => {
                            console.log(
                              `Button clicked for time range: ${value}`
                            );
                            setSelectedReturnArrivalTimes((prev) =>
                              prev.includes(value)
                                ? prev.filter((time) => time !== value)
                                : [...prev, value]
                            );
                          }}
                          className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedReturnArrivalTimes.includes(value)
                              ? "bg-primary text-white"
                              : "bg-[#f3f6fa] text-[#738594]"
                            }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

            {/*multicity filter */}

            {/* Departure Time Filter */}
            {tripInfo.isMuktiCity && (
              <div>
                <h3 className="mb-4 text-md font-medium text-[#05264e]">
                  {tripInfo.isMuktiCity &&
                    tripInfo.routes[indexValue]?.from &&
                    tripInfo.routes[indexValue]?.to ? (
                    <>Departure from {tripInfo.routes[indexValue].from}</>
                  ) : (
                    "Departure Time"
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {timeRanges.map(({ label, value }) => (
                    <button
                      key={value}
                      onClick={() => {
                        console.log(`Button clicked for time range: ${value}`);
                        setSelectedDepartureTimes((prev) =>
                          prev.includes(value)
                            ? prev.filter((time) => time !== value)
                            : [...prev, value]
                        );
                      }}
                      className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedDepartureTimes.includes(value)
                          ? "bg-primary text-white"
                          : "bg-[#f3f6fa] text-[#738594]"
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Arrival Time Filter for Outbound */}
            {tripInfo.isMuktiCity && (
              <div>
                <h3 className="mb-4 text-md font-medium text-[#05264e]">
                  {tripInfo.routes[indexValue]?.from &&
                    tripInfo.routes[indexValue]?.to ? (
                    <>Arrival at {tripInfo.routes[indexValue].to}</>
                  ) : (
                    "Arrival Time"
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {timeRanges.map(({ label, value }) => (
                    <button
                      key={value}
                      onClick={() => {
                        console.log(`Button clicked for time range: ${value}`);
                        setSelectedOutboundArrivalTimes((prev) =>
                          prev.includes(value)
                            ? prev.filter((time) => time !== value)
                            : [...prev, value]
                        );
                      }}
                      className={`p-[8px_24px] rounded-[5px] text-[12px] h-[54px] w-[100%] overflow-hidden border tracking-[0.1px] border-[#f3f6fa] transition-all hover:bg-primary hover:text-white ${selectedOutboundArrivalTimes.includes(value)
                          ? "bg-primary text-white"
                          : "bg-[#f3f6fa] text-[#738594]"
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="px-3 py-3 border-b border-gray-200">
              <div className="flex-between mb-3">
                <h3 className="sidebar-subTitle text-md font-medium">
                  Preferred Airlines
                </h3>
                <button
                  onClick={() => (setSelectedOnwardAirlines([]), setSelectedReturnAirlines([]))}
                  className="text-md font-medium text-[#adb5bd] hover:text-primary transition-all"
                >
                  Reset
                </button>
              </div>
              {/* Onward Airlines Section */}
              {Object.keys(airlinesWithPricesAndCodes.onward).length > 0 && (
                <div>
                  <h4 className="mb-3 text-sm font-medium">Onward Airlines</h4>
                  {Object.keys(airlinesWithPricesAndCodes.onward).map(
                    (airline: string) => (
                      <div
                        key={`onward-${airline}`}
                        className="flex items-center w-full p-[17px_10px] rounded-[10px] overflow-hidden text-white transition-all cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          id={`onward-${airline}`}
                          checked={selectedOnwardAirlines.includes(airline)}
                          onChange={(e: any) => {
                            setSelectedOnwardAirlines(prev =>
                              prev.includes(airline)
                                ? prev.filter(a => a !== airline)
                                : [...prev, airline]
                            );
                          }}
                          className="form-checkbox h-5 w-5 text-[#05264e] border-gray-300 accent-primary rounded"
                        />
                        <Image
                          src={`/AirlinesLogo/${airlinesWithPricesAndCodes.onward[airline].code}.png`}
                          alt={`${airline} Logo`}
                          width={24}
                          height={24}
                          className="w-6 h-6 ml-3 rounded-full"
                        />
                        <label
                          htmlFor={`onward-${airline}`}
                          className="ml-2 text-[#2d3033] text-[13px]"
                        >
                          {airline}
                        </label>
                        <span className="ml-auto text-gray-500">
                          {formatToRupee(
                            airlinesWithPricesAndCodes.onward[airline].price ||
                            0
                          )}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* Return Airlines Section */}
              {Object.keys(airlinesWithPricesAndCodes.return).length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-3 text-sm font-medium">Return Airlines</h4>
                  {Object.keys(airlinesWithPricesAndCodes.return).map(
                    (airline: string) => (
                      <div
                        key={`return-${airline}`}
                        className="flex items-center w-full p-[17px_10px] rounded-[10px] overflow-hidden text-white transition-all cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          id={`return-${airline}`}
                          checked={selectedReturnAirlines.includes(airline)}
                          onChange={(e: any) => {
                            setSelectedReturnAirlines(prev =>
                              prev.includes(airline)
                                ? prev.filter(a => a !== airline)
                                : [...prev, airline]
                            );
                          }}
                          className="form-checkbox h-5 w-5 text-[#05264e] border-gray-300 accent-primary rounded"
                        />
                        <Image
                          src={`/AirlinesLogo/${airlinesWithPricesAndCodes.return[airline].code}.png`}
                          alt={`${airline} Logo`}
                          width={24}
                          height={24}
                          className="w-6 h-6 ml-3 rounded-full"
                        />
                        <label
                          htmlFor={`return-${airline}`}
                          className="ml-2 text-[#2d3033] text-[13px]"
                        >
                          {airline}
                        </label>
                        <span className="ml-auto text-gray-500">
                          {formatToRupee(
                            airlinesWithPricesAndCodes.return[airline].price ||
                            0
                          )}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Filters;
